import { Hidden } from '@mui/material'
import { TailSpin } from 'react-loading-icons'
import { ContentPageNoHeaderFooter } from 'src/components/ContentPage/ContentPageNoHeaderFooter'
import { ContentPageView } from 'src/components/ContentPage/ContentPageView'
import { ContentPagination } from 'src/components/ContentPagination/ContentPagination'
import { SearchField } from 'src/components/global/SearchField'
import { ReCaptcha } from 'src/components/ReCaptcha/ReCaptcha'
import {
  IContentEntityBySlug,
  IDocumentTypeBySolutionIdQuery,
  IEnum_Content_Contenttype,
  useDocumentTypeBySolutionIdQuery,
  useVendorAnalysisBySolutionIdQuery,
} from 'src/graphql/generated/hooks'

interface VendorAnalysisContainerProps {
  solutionId: string
  analysisTotal?: number
  documentType?: string
}

const SolutionContentList: React.FC<{ solutionId: string; documentType: string }> = ({ solutionId, documentType }) => {
  return (
    <ReCaptcha>
      <div className="flex flex-col justify-center w-full md:pl-0 md:pr-0">
        <div className="flex flex-row justify-center w-full">
          <div className="!max-w-[1100px] flex flex-col md:flex-row justify-between w-full">
            {/* {analysisTotal > 2 && ( */}
            <Hidden mdDown>
              <div className="w-1/4 pr-10 flex flex-col">
                <div className="md:my-[1rem] md:mt-0 mt-[16px] md:mb-[4px] mb-[20px] min-h-[28px] mr-[180px]"></div>
                <SearchField className="my-6" />
              </div>
            </Hidden>
            {/* )} */}

            <Hidden mdUp>
              <div className="w-full flex flex-col">
                <SearchField className="md:my-[1.4rem] mt-[1.4rem]" />
              </div>
            </Hidden>

            {documentType && documentType === 'Product Briefing' ? (
              <ContentPagination
                key={solutionId + '-' + documentType + 'ContentPaginationProductBriefing'}
                documentType={documentType}
                showDocumentType={true}
                preAppliedFilters={{ resourceSolutionIds: solutionId }}
                filterProps={[]}
                fromPage="ContentPaginationProductBriefing"
              />
            ) : (
              <ContentPagination
                key={solutionId + '-' + 'ContentPagination'}
                contentType={IEnum_Content_Contenttype.VendorAnalysis}
                preAppliedFilters={{ resourceSolutionIds: solutionId }}
                filterProps={[]}
                fromPage="SolutionVendorAnalysisContainer"
              />
            )}
          </div>
        </div>
      </div>
    </ReCaptcha>
  )
}

const SolutionVendorAnalysisContainer: React.FC<VendorAnalysisContainerProps> = ({
  solutionId,
  analysisTotal = 0,
  documentType = '',
}) => {
  if (analysisTotal > 1) {
    return <SolutionContentList solutionId={solutionId} documentType={documentType} />
  } else {
    let contentsData: IDocumentTypeBySolutionIdQuery | undefined
    let loading: boolean

    if (documentType && documentType === 'Product Briefing') {
      const { data: productBriefingData, loading: productBriefingLoading } = useDocumentTypeBySolutionIdQuery({
        variables: { solutionId, documentType: 'Product Briefing' },
      })
      contentsData = productBriefingData
      loading = productBriefingLoading
    } else {
      const { data: vendorAnalysisData, loading: vendorLoading } = useVendorAnalysisBySolutionIdQuery({
        variables: { solutionId },
      })
      contentsData = vendorAnalysisData
      loading = vendorLoading
    }

    const items = contentsData?.contents || []
    const contentSlug = items.length > 0 ? items[0]?.slug : null
    const contentSnippet = items.length > 0 ? items[0]?.snippet : null
    const isProductBriefing = documentType === 'Product Briefing'

    // analysisTotal comes from the statically generated page (ISR), so it can be stale for a while after
    // Product Briefing content is added to or removed from the solution in Strapi. Trust the live result here.
    if (isProductBriefing && !loading && items.length > 1) {
      return <SolutionContentList solutionId={solutionId} documentType={documentType} />
    }

    return (
      <>
        {loading && (
          <div className="w-full flex items-center justify-center my-[40px]">
            <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
          </div>
        )}
        {!loading && contentSlug && (isProductBriefing || contentSnippet) && (
          <>
            {documentType && documentType === 'Product Briefing' ? (
              <ContentPageNoHeaderFooter
                slug={contentSlug}
                content={contentsData as IContentEntityBySlug | undefined}
                origin={`/contents/${contentSlug}/`}
                relatedContents={[]}
              />
            ) : (
              <ContentPageView
                key={solutionId + '-' + 'ContentPageView'}
                slug={contentSlug}
                content={contentsData as IContentEntityBySlug | undefined}
                contentSnippet={contentSnippet || ''}
              />
            )}
          </>
        )}
        {isProductBriefing && !loading && !contentSlug && (
          <div className="w-full flex items-center justify-center my-[40px] font-primary font-medium">
            No content found
          </div>
        )}
      </>
    )
  }
}

export default SolutionVendorAnalysisContainer
